/* The merchant dashboard, in the artboard's light register.
 *
 * SOURCE: `Damga Merchant Dashboard v2.dc.html` (SHA-256 406d0eca…, 165,812
 * bytes): white ground, ink #08282f, accent #0e7490, a 232px sidebar and a
 * content column, the same three faces as every other surface.
 *
 * A SIDEBAR, NOT A TOP NAV, which is what the artboard draws and what a dense
 * tool somebody opens daily should have.
 *
 * DEPARTURES, ALL NAMED. The artboard is one 1440 x 960 frame; this is a real
 * page, so the column is fluid, the sidebar collapses to a row of tabs under
 * 900px, and nothing is positioned absolutely. Its mono labels sit at 9 and
 * 10px; here uppercase mono labels are 11px and words 12px or more (the floor
 * was 10px until the review of 18 September, finding 19). Its tables assume
 * data that exists, so every one of them has an empty state underneath it in
 * the same shape; on a phone each row is a block (see table.table-stack).
 */
export const dashCss = `
@font-face {
  font-family: "Schibsted Grotesk";
  src: url("/fonts/SchibstedGrotesk-variable-latin.woff2") format("woff2");
  font-weight: 400 900;
  font-display: swap;
}
@font-face {
  font-family: "Bricolage Grotesque";
  src: url("/fonts/BricolageGrotesque-variable-latin.woff2") format("woff2");
  font-weight: 400 800;
  font-display: swap;
}
@font-face {
  font-family: "DM Mono";
  src: url("/fonts/DMMono-500-latin.woff2") format("woff2");
  font-weight: 400 600;
  font-display: swap;
}

:root {
  color-scheme: light;
  --font-sans: "Schibsted Grotesk", system-ui, -apple-system, sans-serif;
  --font-mono: "DM Mono", ui-monospace, SFMono-Regular, monospace;
  --font-display: "Bricolage Grotesque", "Schibsted Grotesk", sans-serif;

  --ink: #08282f;
  --ink-soft: #33565f;
  --ink-faint: rgba(8, 40, 47, 0.74);
  --accent: #0b5c74;
  --seal: #0e7490;
  --sky: #38bdf8;
  --fill: #5eead4;
  --ok: #0c6543;
  --warn: #845009;
  --danger: #993524;

  --paper: #ffffff;
  --panel: #f7fafb;
  --line: rgba(8, 40, 47, 0.12);
  --line-strong: rgba(8, 40, 47, 0.18);
  --tint: rgba(8, 40, 47, 0.05);
  --grad-accent: linear-gradient(160deg, #5eead4, #4dd6e8 55%, #38bdf8);
  --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
}

*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; background: var(--paper); }
body { font-family: var(--font-sans); color: var(--ink); -webkit-font-smoothing: antialiased; }
h1, h2, h3 { font-family: var(--font-display); font-optical-sizing: auto; margin: 0; }
p { margin: 0; }
button { font: inherit; color: inherit; }
:focus-visible { outline: 2px solid var(--seal); outline-offset: 2px; }
table { border-collapse: collapse; width: 100%; }

.app { display: flex; min-height: 100dvh; }

/* --- the sidebar ------------------------------------------------------------ */
.side {
  flex: none;
  width: 232px;
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border-right: 1px solid var(--line);
}
.side-top { display: flex; align-items: center; justify-content: space-between; }
.side-brand { display: flex; align-items: center; gap: 10px; padding: 20px 18px 18px; }
.side-menu { display: none; }
.side-panel { display: flex; flex-direction: column; flex: 1 1 auto; }
.mark {
  position: relative;
  display: block;
  flex: none;
  width: 22px;
  height: 22px;
  background: var(--seal);
  border-radius: 6px 6px 6px 1px;
  overflow: hidden;
}
.mark::before, .mark::after { content: ""; position: absolute; top: 6px; height: 11px; background: #eaf6f8; }
.mark::before { left: 6px; width: 7px; border-radius: 0 999px 999px 0; }
.mark::after { left: 4px; width: 2px; }
.side-word { font: 600 14px/1 var(--font-sans); letter-spacing: -0.025em; }
.side-nav { padding: 0 12px; display: grid; gap: 4px; align-content: start; flex: 1 1 auto; }
.side-link {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  /* 44px to press, at every size (review, finding 17: they were 35). */
  min-height: 44px;
  padding: 11px 13px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: var(--ink-soft);
  font: 500 13px/1 var(--font-sans);
  text-align: start;
  cursor: pointer;
}
.side-link:hover { background: rgba(8, 40, 47, 0.05); color: var(--ink); }
.side-link[aria-current="page"] { background: var(--paper); color: var(--ink); font-weight: 600; box-shadow: 0 6px 14px -10px rgba(8, 40, 47, 0.4), inset 0 0 0 1px var(--line); }
.side-foot { padding: 14px 16px; border-top: 1px solid var(--line); display: grid; gap: 8px; }
.side-who { font: 500 12px/1.4 var(--font-mono); color: var(--ink-faint); overflow-wrap: anywhere; }

/* Under 900px: one bar, and a menu beneath it (2026-09-18). It used to be a row
   of eleven sections that scrolled sideways with no sign that it did, so four
   were visible at 390 and "Counter card" was cut mid-word at 820. The same
   screens, reached from a list that shows all of them at once. */
@media (max-width: 900px) {
  .app { flex-direction: column; }
  .side { width: auto; border-right: 0; border-bottom: 1px solid var(--line); }
  .side-top { padding: 8px 12px 8px 18px; }
  .side-brand { padding: 0; }
  .side-menu {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 0 18px;
    border: 1px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
    color: var(--ink);
    font: 600 13px/1 var(--font-sans);
    cursor: pointer;
  }
  .side-menu[aria-expanded="true"] { background: var(--ink); color: var(--paper); border-color: var(--ink); }
  .side-panel { display: none; }
  .side-panel[data-open] { display: flex; border-top: 1px solid var(--line); }
  .side-nav { padding: 8px 12px; gap: 2px; }
  .side-foot { display: flex; align-items: center; justify-content: space-between; }
}

/* --- the content column ------------------------------------------------------ */
/* ONE COLUMN THAT MAY SHRINK, here and on .panel and .stack. A grid's implicit
   column is sized to its widest content, so any table wider than a phone pushed
   the whole page sideways instead of scrolling in its own .table-wrap. The team
   table's actions found it at 390; the other tables had only happened to fit. */
.main { flex: 1 1 auto; min-width: 0; padding: 26px 32px 56px; display: grid; grid-template-columns: minmax(0, 1fr); gap: 20px; align-content: start; }
@media (max-width: 700px) { .main { padding: 20px 16px 40px; } }
.head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.eyebrow { font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--accent); margin: 0 0 7px; }
.title { font: 600 27px/1.1 var(--font-display); letter-spacing: -0.02em; }
.lead { font: 400 14px/1.55 var(--font-sans); color: var(--ink-soft); max-width: 62ch; }

.range { display: flex; gap: 6px; padding: 4px; border-radius: 14px; background: var(--tint); border: 1px solid var(--line); }
/* 44px to press (review finding 16: they were 28). */
.range button { min-height: 44px; border: 0; border-radius: 10px; background: transparent; padding: 0 14px; font: 600 13px/1 var(--font-sans); color: var(--ink-faint); cursor: pointer; }
.range button[aria-pressed="true"] { background: var(--paper); color: var(--ink); box-shadow: inset 0 0 0 1px var(--line); }

.stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }
.stat { padding: 16px 18px; border-radius: 18px; background: var(--panel); border: 1px solid var(--line); }
.stat-v { font: 500 27px/1 var(--font-mono); font-variant-numeric: tabular-nums; margin: 0 0 8px; }
.stat-k { font: 600 11px/1.2 var(--font-mono); letter-spacing: 0.07em; text-transform: uppercase; color: var(--ink-faint); }

.panel { padding: 20px 22px; border-radius: 20px; background: var(--paper); border: 1px solid var(--line); display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; }
.panel-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.panel-title { font: 600 15px/1.2 var(--font-sans); }
.cols { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; align-items: start; }

.label { font: 600 11px/1.2 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--ink-faint); }
.mono { font-family: var(--font-mono); font-variant-numeric: tabular-nums; }

/* --- tables ------------------------------------------------------------------ */
/* position: relative, so anything absolutely placed inside a table -- the
   visually hidden header on the team's actions column -- is contained by this
   scroller. Without it, that 1px label escaped to the table's far edge and made
   the page itself 563px wide at 390. */
.table-wrap { overflow-x: auto; position: relative; }
th {
  text-align: start;
  padding: 0 12px 10px;
  font: 600 11px/1.2 var(--font-mono);
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--ink-faint);
  border-bottom: 1px solid var(--line);
  white-space: nowrap;
}
td { padding: 13px 12px; border-bottom: 1px solid var(--line); font: 400 13px/1.4 var(--font-sans); vertical-align: top; }
td.num, th.num { text-align: end; font-family: var(--font-mono); font-variant-numeric: tabular-nums; }
tbody tr:hover { background: var(--tint); }
.cell-name { font-weight: 600; }
.cell-sub { display: block; font: 500 12px/1.4 var(--font-mono); color: var(--ink-faint); margin-top: 3px; }

/* A TABLE ON A PHONE IS A LIST OF BLOCKS (review finding 15). Customers,
   rewards and the import were tables that scrolled sideways in a box that did
   not say it scrolled: the last visit was cut at the edge. Under 600px each row
   is a block: its lead (the person, or the number) across the top, and every
   other cell under its own heading, three to a line. The headings stay in the
   markup for screen readers; the roles on every element keep the table a
   table to them when it is laid out as blocks. */
@media (max-width: 600px) {
  table.table-stack, table.table-stack tbody, table.table-stack tr, table.table-stack td { display: block; }
  table.table-stack thead {
    position: absolute; width: 1px; height: 1px; overflow: hidden;
    clip: rect(0 0 0 0); white-space: nowrap;
  }
  table.table-stack tr {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px 14px;
    padding: 14px 2px;
    border-bottom: 1px solid var(--line);
  }
  table.table-stack tbody tr:hover { background: none; }
  table.table-stack td { padding: 0; border: 0; text-align: start; overflow-wrap: anywhere; }
  table.table-stack td[data-lead] { grid-column: 1 / -1; order: -1; }
  table.table-stack td[data-label]::before {
    content: attr(data-label);
    display: block;
    margin-bottom: 4px;
    font: 600 11px/1.2 var(--font-mono);
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  table.table-stack td:not([data-label]):not([data-lead]) { display: flex; align-items: flex-end; justify-content: flex-end; }
}

.chip { display: inline-flex; align-items: center; padding: 5px 9px; border-radius: 999px; background: var(--tint); font: 600 11px/1 var(--font-mono); letter-spacing: 0.06em; text-transform: uppercase; color: var(--ink-soft); white-space: nowrap; }
.chip[data-tone="good"] { background: rgba(12, 101, 67, 0.12); color: var(--ok); }
.chip[data-tone="owed"] { background: rgba(14, 116, 144, 0.12); color: var(--accent); }
.chip[data-tone="warn"] { background: rgba(132, 80, 9, 0.12); color: var(--warn); }

.rows { display: grid; gap: 10px; list-style: none; margin: 0; padding: 0; }
.row { display: grid; grid-template-columns: 34px 1fr auto; gap: 12px; align-items: center; padding: 12px 14px; border-radius: 14px; background: var(--panel); border: 1px solid var(--line); }
.dot { width: 34px; height: 34px; border-radius: 999px; display: flex; align-items: center; justify-content: center; background: rgba(14, 116, 144, 0.14); color: var(--accent); font: 600 13px/1 var(--font-mono); }
.dot[data-kind="reward"] { background: rgba(94, 234, 212, 0.35); color: #04282e; }
.dot[data-kind="back"] { background: rgba(153, 53, 36, 0.1); color: var(--danger); }
.row-title { font: 500 13px/1.3 var(--font-sans); }
.row-meta { font: 500 12px/1.3 var(--font-mono); color: var(--ink-faint); margin-top: 3px; }
.row-value { font: 600 12px/1 var(--font-mono); color: var(--ink-soft); white-space: nowrap; }

.bar { height: 8px; border-radius: 999px; background: var(--tint); overflow: hidden; }
.bar-fill { display: block; height: 100%; border-radius: 999px; background: var(--grad-accent); transform-origin: left center; transform: scaleX(var(--pct, 0)); }

/* --- empty states, which most of this dashboard is on day one ---------------- */
.empty { padding: 36px 24px; border-radius: 20px; background: var(--panel); border: 1px dashed var(--line-strong); display: grid; gap: 8px; justify-items: start; }
.empty-title { font: 600 16px/1.3 var(--font-sans); }
.empty-body { font: 400 13.5px/1.55 var(--font-sans); color: var(--ink-soft); max-width: 52ch; }

/* --- controls ---------------------------------------------------------------- */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  height: 44px;
  padding: 0 18px;
  border: 0;
  border-radius: 14px;
  font: 600 13px/1 var(--font-sans);
  cursor: pointer;
  transition: transform 150ms var(--ease-out);
}
.btn:active:not(:disabled) { transform: scale(0.98); }
.btn-primary { background: var(--grad-accent); color: #04282e; box-shadow: 0 8px 18px -12px rgba(14, 116, 144, 0.8); }
.btn-secondary { background: var(--paper); color: var(--ink); border: 1px solid var(--line-strong); }
.btn-quiet { background: transparent; color: var(--ink-faint); }
.btn:disabled { opacity: 0.55; cursor: default; }

.field { display: grid; gap: 7px; max-width: 420px; }
.field-label { font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--ink-faint); }
.input { height: 46px; padding: 0 14px; border-radius: 12px; border: 1px solid var(--line-strong); background: var(--paper); font: 400 15px/1 var(--font-sans); color: var(--ink); }
.input:focus-visible { border-color: var(--seal); box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.18); outline: 0; }
/* The browser's own placeholder grey measured 2.35:1 on this ground, which the
   proof caught. The same defect the landing had, and the same fix. */
.input::placeholder { color: rgba(8, 40, 47, 0.62); }
.search { max-width: 320px; }
.error { color: var(--danger); font: 500 13px/1.45 var(--font-sans); }
.note { font: 400 13px/1.55 var(--font-sans); color: var(--ink-soft); }
.fine { font: 400 12px/1.5 var(--font-sans); color: var(--ink-faint); }

.banner { padding: 12px 16px; border-radius: 14px; background: rgba(132, 80, 9, 0.08); border: 1px solid rgba(132, 80, 9, 0.3); color: var(--warn); font: 500 13px/1.45 var(--font-sans); }

/* --- sign in: the merchant's username and password (2026-09-18) ---------------
 * The shared screen from @damga/ds, which brings layout only; this is its light
 * register. Google, Microsoft and the emailed-code sign-in are gone from here, and
 * their provider buttons with them. */
.msi { min-height: 100dvh; display: grid; place-items: center; padding: 40px 16px; box-sizing: border-box; background: linear-gradient(168deg, #ffffff, #f7fafb 60%, #eef4f5); }
.msi-card { width: 100%; max-width: 420px; display: grid; gap: 18px; padding: 28px; border-radius: 22px; background: var(--paper); border: 1px solid var(--line); box-shadow: 0 30px 60px -40px rgba(8, 40, 47, 0.4); box-sizing: border-box; }
.msi-form { display: grid; gap: 16px; }
.msi-title { margin: 0; font: 600 24px/1.2 var(--font-display); text-wrap: balance; }
.msi-body { margin: 0; font: 400 14px/1.55 var(--font-sans); color: var(--ink-soft); }
.msi-field { display: grid; gap: 7px; }
.msi-label { font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--ink-faint); }
.msi-input { box-sizing: border-box; width: 100%; height: 48px; padding: 0 14px; border-radius: 12px; border: 1px solid var(--line-strong); background: var(--paper); font: 400 16px/1 var(--font-sans); color: var(--ink); }
.msi-input:focus-visible { border-color: var(--seal); box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.18); outline: 0; }
.msi-input[readonly] { background: var(--panel); color: var(--ink-soft); }
.msi-password { position: relative; display: block; }
.msi-password .msi-input { padding-right: 88px; }
/* The full height of the 48px field, so it is a 48px target; the ground is transparent, so it looks the same. */
.msi-reveal { position: absolute; right: 2px; top: 0; bottom: 0; min-width: 76px; border: 0; border-radius: 10px; background: transparent; color: var(--accent); font: 600 13px/1 var(--font-sans); cursor: pointer; }
.msi-code { font: 500 24px/1 var(--font-mono); letter-spacing: 0.3em; text-align: center; font-variant-numeric: tabular-nums; }
.msi-problem { margin: 0; color: var(--danger); font: 500 13px/1.45 var(--font-sans); }
.msi-submit { height: 48px; border: 0; border-radius: 14px; background: var(--grad-accent); color: #04282e; font: 600 14px/1 var(--font-sans); cursor: pointer; box-shadow: 0 8px 18px -12px rgba(14, 116, 144, 0.8); }
.msi-submit[aria-busy="true"] { cursor: progress; }
.msi-link { min-height: 44px; border: 0; background: transparent; color: var(--accent); font: 600 13px/1 var(--font-sans); text-decoration: underline; cursor: pointer; justify-self: center; }

/* --- the owner's forms: the team, and the first card ---------------------------- */
/* minmax(0, 1fr), not the default auto: a grid item is at least as wide as its
   content, so a wide table inside pushed the whole page sideways at 390 instead
   of scrolling in its own .table-wrap. Found by the dashboard's render proof. */
.stack { display: grid; gap: 16px; grid-template-columns: minmax(0, 1fr); }
.stamps-row { display: flex; flex-wrap: wrap; gap: 8px; }
.stamp-mark { width: 34px; height: 34px; border-radius: 999px; display: grid; place-items: center; background: var(--tint); border: 1px solid var(--line-strong); font: 500 12px/1 var(--font-mono); color: var(--ink-soft); }
.form-panel { display: grid; gap: 14px; }
.form { display: grid; gap: 14px; max-width: 420px; }
.form .btn { justify-self: start; }
.form-problem { color: var(--danger); font: 500 13px/1.45 var(--font-sans); }
.form-said { color: var(--ok); font: 500 13px/1.45 var(--font-sans); }
.row-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; }

/* --- the team, as people (2026-09-18) --------------------------------------- */
/* One set of columns for every person (subgrid), so the owner's states line up
   under the cashier's even though the owner has no actions. */
.people {
  list-style: none; margin: 0; padding: 0;
  display: grid;
  grid-template-columns: minmax(12rem, 1fr) minmax(0, 1.3fr) auto;
}
.person {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  align-items: center;
  gap: 12px 20px;
  padding: 14px 2px;
  border-top: 1px solid var(--line);
}
.person:first-child { border-top: 0; padding-top: 4px; }
.person-who { display: grid; gap: 4px; min-width: 0; }
.person-who .cell-name { overflow-wrap: anywhere; }
.person-role { font: 500 13px/1.3 var(--font-sans); color: var(--ink-faint); }
.person-state { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; }
.person-pair { display: inline-flex; align-items: center; gap: 8px; }
.person-k { font: 500 12px/1 var(--font-sans); color: var(--ink-faint); }
.person .row-actions { justify-content: flex-end; }
.person .btn { white-space: nowrap; }
@media (max-width: 760px) {
  .people { grid-template-columns: minmax(0, 1fr); }
  .person { grid-template-columns: minmax(0, 1fr); gap: 10px; }
  .person .row-actions { justify-content: flex-start; margin-left: -12px; }
}
.row-said { font: 500 12px/1.4 var(--font-sans); color: var(--ink-soft); }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }

/* --- the counter card, on screen and on paper -------------------------------
 *
 * The same markup does both. On screen the two sheets are previews at a
 * readable size; on paper they are the sheets. Nothing is duplicated for print,
 * because two copies of one card is how the printed one stops matching the one
 * a merchant approved.
 *
 * mm THROUGHOUT, not px: this is the one thing in the product whose real size
 * is a physical size, and a card designed in pixels comes out of a printer at
 * whatever the browser felt like. */
/* A5 is 148mm, which is wider than a phone. It used to scroll inside its own
   box, and at 430 that box cut the shop's name to "Fixture Roaste" (review
   finding 18): the preview hid the one thing the owner came to check.

   ONE UNIT, --mm. Every size on the card is a multiple of it. In print it is
   exactly 1mm, so the sheet is A5 to the millimetre; on the screen it is
   whichever is smaller, 1mm or the column's width over 148, so the preview is
   the same card, whole, at the size the screen has. Nothing is drawn twice. */
.sheets { display: grid; gap: 20px; justify-items: start; container-type: inline-size; padding-bottom: 4px; }
.sheet {
  --mm: min(1mm, 100cqi / 148);
  width: calc(148 * var(--mm));
  min-height: calc(210 * var(--mm));
  padding: calc(18 * var(--mm)) calc(16 * var(--mm));
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 4px;
  box-shadow: 0 20px 44px -34px rgba(8, 40, 47, 0.5);
  display: flex;
  flex-direction: column;
}
.sheet-in { display: flex; flex-direction: column; gap: calc(7 * var(--mm)); flex: 1 1 auto; }
.sheet-mark { font: 600 calc(3 * var(--mm))/1 var(--font-mono); letter-spacing: 0.22em; text-transform: uppercase; color: var(--accent); }
.sheet-shop { font: 600 calc(14 * var(--mm))/1.05 var(--font-display); letter-spacing: -0.02em; overflow-wrap: break-word; margin: calc(2 * var(--mm)) 0 0; }
.sheet-collect { font: 400 calc(6 * var(--mm))/1.3 var(--font-sans); color: var(--ink-soft); }
/* The reward is the sentence somebody reads from a queue, so it is the only
   thing on the card allowed to be loud. */
.sheet-reward {
  font: 600 calc(9 * var(--mm))/1.2 var(--font-display);
  letter-spacing: -0.015em;
  padding: calc(7 * var(--mm)) 0;
  border-top: calc(0.6 * var(--mm)) solid var(--ink);
  border-bottom: calc(0.6 * var(--mm)) solid var(--ink);
  margin: calc(1 * var(--mm)) 0;
}
.sheet-join { font: 600 calc(6.5 * var(--mm))/1.25 var(--font-sans); margin-top: auto; }
.sheet-how { font: 400 calc(4.4 * var(--mm))/1.45 var(--font-sans); color: var(--ink-soft); }
.sheet-check { font: 400 calc(4.4 * var(--mm))/1.45 var(--font-sans); color: var(--ink-faint); }

.sheet[data-kind="sticker"] { justify-content: center; align-items: center; gap: calc(10 * var(--mm)); }
.sticker {
  width: calc(105 * var(--mm));
  height: calc(74 * var(--mm));
  padding: calc(10 * var(--mm));
  border: calc(0.6 * var(--mm)) dashed var(--line-strong);
  border-radius: calc(3 * var(--mm));
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: calc(3 * var(--mm));
}
.sticker-shop { font: 600 calc(9 * var(--mm))/1.05 var(--font-display); letter-spacing: -0.02em; overflow-wrap: break-word; margin: calc(1 * var(--mm)) 0 0; }
.sticker-collect { font: 400 calc(4.6 * var(--mm))/1.3 var(--font-sans); color: var(--ink-soft); }
.sticker-reward { font: 600 calc(5.6 * var(--mm))/1.25 var(--font-sans); }
.cut { font: 500 calc(3.2 * var(--mm))/1 var(--font-mono); letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-faint); }

@media print {
  /* Everything that is not the card goes: the sidebar, the heading, the
     explanation above it. A printed sheet carrying a navigation column is the
     tell that nobody printed it before shipping it. */
  @page { size: A5 portrait; margin: 0; }
  html, body { background: #ffffff; }
  .side, .head, .no-print, .banner { display: none !important; }
  .app { display: block; }
  .main { padding: 0; gap: 0; display: block; }
  .sheets { display: block; gap: 0; overflow: visible; padding: 0; }
  .sheet {
    --mm: 1mm;
    width: 148mm;
    height: 210mm;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    break-after: page;
    page-break-after: always;
  }
  .sheet:last-child { break-after: auto; page-break-after: auto; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 1ms !important; transition-duration: 1ms !important; }
}

.visually-hidden { position: absolute !important; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
`;
